import { Add, Delete } from '@mui/icons-material';
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import { formatMoney } from 'lib/functions/utils';
import React, { useCallback } from 'react';

// Type Definitions
interface SubPrductAssingnationProps {
  concentrations: any[];
  setConcentrations: (concentrations: any[]) => void;
  amount: number;
  subProducts: any[];
}

// Component Definitions
const SubProductItem = (props: any) => {
  const {
    index,
    concentration,
    concentrations,
    setConcentrations,
    deleteConcentration,
    handleSelectSubProduct,
    availableSubProducts,
  } = props;

  const handleChangeAmount = (e: any, index: number) => {
    const value = e.target.value.split('.').join('').split('$').join('');
    const name = e.target.name;

    if (isNaN(value)) return;
    const newArray = [...concentrations];
    newArray[index][name] = Number(value) || 0;
    setConcentrations(newArray);
  };

  const handleChangeDecimal = (e: any, index: number) => {
    const value = e.target.value;
    const name = e.target.name;

    // Permitir solo números y un punto decimal
    if (!/^\d*\.?\d*$/.test(value)) return;

    const newArray = [...concentrations];
    newArray[index][name] = value;
    setConcentrations(newArray);
  };

  const handleChangeRate = (e: any, index: number) => {
    const value = e.target.value;
    const name = e.target.name;
    if (isNaN(value)) return;
    if (value < 0) return;
    if (value > 100) return;
    const newArray = [...concentrations];
    newArray[index][name] = value;
    setConcentrations(newArray);
  };

  return (
    <div key={index} style={{ display: 'flex' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={2}>
          <FormControl key={index} variant="outlined" fullWidth>
            <InputLabel id="filter-label">Seleccionar</InputLabel>
            <Select
              labelId="filter-label"
              fullWidth
              id="Filtrar por estado"
              value={concentration.id}
              onChange={(e) => handleSelectSubProduct(e, index)}
              label="Seleccionar"
            >
              {availableSubProducts(index)
                .filter((x: any) => x.id !== 'warranty-premium')
                .map((x: any, index: any) => (
                  <MenuItem key={index} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <TextField
            autoFocus
            margin="dense"
            label="C. Variable (%)"
            name="operationCostVariable"
            value={concentration.operationCostVariable}
            onChange={(e) => handleChangeRate(e, index)}
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            autoFocus
            margin="dense"
            label="Dcto. tasa variable (%)"
            name="discountRateVariable"
            value={concentration.discountRateVariable}
            onChange={(e) => handleChangeRate(e, index)}
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            autoFocus
            margin="dense"
            label="C. Fija (UF)"
            name="operationCostFixed"
            value={concentration.operationCostFixed}
            onChange={(e) => handleChangeDecimal(e, index)}
            inputProps={{
              step: '0.01',
              min: '0',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            autoFocus
            margin="dense"
            label="Dcto. tasa fija (%)"
            name="discountRateFixed"
            value={concentration.discountRateFixed}
            onChange={(e) => handleChangeRate(e, index)}
            fullWidth
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            autoFocus
            margin="dense"
            label="Monto ($)"
            name="amount"
            value={formatMoney(concentration.amount)}
            onChange={(e) => handleChangeAmount(e, index)}
            fullWidth
          />
        </Grid>
      </Grid>
      <IconButton
        style={{ marginLeft: '10px' }}
        onClick={() => deleteConcentration(index)}
      >
        <Delete />
      </IconButton>
    </div>
  );
};

const SubPrductAssingnation = (props: SubPrductAssingnationProps) => {
  const { concentrations, setConcentrations, amount, subProducts } = props;
  const availableSubProductsCount = subProducts.filter(
    (x: any) => x.id !== 'warranty-premium',
  ).length;
  const canAddMore = concentrations.length < availableSubProductsCount;

  const addEmptyConcentration = useCallback(() => {
    if (canAddMore) {
      setConcentrations([...concentrations, {}]);
    }
  }, [concentrations, setConcentrations, canAddMore]);

  const deleteConcentration = (index: number) => {
    const newArray = [...concentrations];
    newArray.splice(index, 1);
    setConcentrations(newArray);
  };

  const handleSelectSubProduct = (e: any, index: number) => {
    const newArray = [...concentrations];
    const subProduct = subProducts.find((x: any) => x.id === e.target.value);
    if (!subProduct) return;
    newArray[index].subProductName = subProduct.name;
    newArray[index].id = subProduct.id;
    setConcentrations(newArray);
  };

  const availableSubProducts = (index: number) =>
    subProducts.filter(
      (x: any) =>
        !concentrations.map((x: any) => x.id).includes(x.id) ||
        concentrations[index].id === x.id,
    );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography>
          Acá podrás asignar los detalles de tus subproductos
        </Typography>
        <div style={{ padding: '10px 0px', textAlign: 'right' }}>
          <CustomButton
            color="secondary"
            onClick={addEmptyConcentration}
            disabled={!canAddMore}
          >
            <Add /> Sub producto
          </CustomButton>
        </div>
      </div>
      <div style={{ padding: '10px 0px' }}>
        {concentrations.map((concentration: any, index: any) => (
          <div key={index}>
            <SubProductItem
              key={index}
              index={index}
              concentration={concentration}
              concentrations={concentrations}
              setConcentrations={setConcentrations}
              deleteConcentration={deleteConcentration}
              handleSelectSubProduct={handleSelectSubProduct}
              availableSubProducts={availableSubProducts}
              amount={amount}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default SubPrductAssingnation;
